'use strict';
import moment from 'moment';
import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { get } from 'lodash';
import { of } from 'rxjs/observable/of';
import { switchMap, take } from 'rxjs/operators';

import { Filter } from '../../filter/classes/filter';
import { FilterService } from '../../filter/services/filter.service';
import { IspmHttpClient } from '@ispm/core/services/ispm-http-client';
import { SimpleSearchOrigin, ISimpleSearchWorklistMappingItem, ISimpleSearchQuery, ISimpleSearchConfig, ISimpleSearchFilterParameter } from '../interfaces/simple-search.interface';
import {StringUtils} from "@ispm/shared/utils/String.utils";

export const SIMPLE_QUERY = 'simple';

@Injectable()
export class SimpleSearchService {
  constructor(
    private _ispmHttpClient: IspmHttpClient,
    private filterService: FilterService
  ) {
  }

  public getQuery(
    simpleSearchConfig: ISimpleSearchConfig,
    queryOrigin?: string,
    searchText?: string,
    page?: number,
    sortField?: string,
    sortOrder?: number,
    filterObj?: Filter,
    countOnly: boolean = false,
    allItemFilterValue: string = '',
    associationItems: any[] = []
  ) {
    const PATHS_DELIMITER = `%23%24%23`;
    const LIST_DELIMITER = `%7C`;
    const queryParameters = { } as ISimpleSearchQuery;

    if (!!simpleSearchConfig.fieldslist) { // If fieldslist is missing, getting all the data
      queryParameters._fieldslist = simpleSearchConfig.fieldslist;
    }

    if (!!simpleSearchConfig.filters) { // Adding predefined filters (e.g. application type)
      simpleSearchConfig.filters.forEach(filter => queryParameters[filter.paramName] = filter.paramValue);
    }

    if (countOnly) { // Sort, count and page are not needed in count only queries
      queryParameters._countOnly = true;
    } else {
      const sortOrderParameterName: string = sortOrder === -1 ? '_sort:desc' : '_sort:asc'; // sort param format: _sort:desc: published
      queryParameters[sortOrderParameterName] = !!sortField ? sortField : simpleSearchConfig.sortByField;
      queryParameters._count = !!simpleSearchConfig.chunkSize ? simpleSearchConfig.chunkSize : 60;
      queryParameters._page = page;
    }

    if (!!simpleSearchConfig.active) {
      queryParameters.active = simpleSearchConfig.active;
    }

    if (searchText) {
      if(get(simpleSearchConfig,'useMultipleSearch',false)) {
        searchText = StringUtils.getChunks(searchText).join(',');
      }
      searchText = searchText.trim();
      let searchParameters: string = '';

      if (simpleSearchConfig.searchableFields.length > 0) {
        // Search format: searchAgg=name%23%24%23identifier%23%24%23birthDate%23%24%23gender%7C$ABCD&
        simpleSearchConfig.searchableFields.forEach(field => {
          searchParameters += field.trim() + PATHS_DELIMITER;
        });
        searchParameters = searchParameters.substring(0, searchParameters.length - PATHS_DELIMITER.length); // Remove last delimiter
        const spacePos: number = searchText.indexOf(' ');

        if (spacePos > 1) {
          // If search text contain space sending an additional searchAgg parameter with @@ (@@searchAgg) because it is not possible to send two parameters with the same name.
          // This way the search will filter for results in which the two parameters exists.
          // For example, if searching for an items named "aaa bbb", the following searches will find it: 'aaa', 'bbb' 'aa bb', 'bb aa' BUT 'aa bXb' will not find it.

          // Search format in case there is a space in the search text: searchAgg=identifier,MRN%23%24%23name,0,text%7Cbbb@@searchAgg=identifier,MRN%23%24%23name,0,text%7Caaa
          const firstSearchParam: string = searchText.substring(0, spacePos).trim();
          const secondSearchParam: string = searchText.substring(spacePos, searchText.length).trim();
          // tslint:disable-next-line:max-line-length
          searchParameters = searchParameters + LIST_DELIMITER + firstSearchParam + '@@searchAgg=' + searchParameters + LIST_DELIMITER + secondSearchParam;
        } else {
          searchParameters = searchParameters + LIST_DELIMITER + searchText;
        }
      } else {
        console.error('SimpleSearchService->search: no searchableFields were defined in workflowTemplate');
        return;
      }

      queryParameters.searchAgg = searchParameters;
    }

    if (queryOrigin === SimpleSearchOrigin.ADVANCED_SEARCH) { // Filters should be ignored in advanced search
      return of({
        resource: simpleSearchConfig.resourceType,
        type: SIMPLE_QUERY,
        params: queryParameters
      });
    }

    if (queryOrigin === SimpleSearchOrigin.ALL_ITEMS) {
      const allItemFilterParamName: string = !!simpleSearchConfig.allItemFilterParamName ? simpleSearchConfig.allItemFilterParamName : 'patientId';
      queryParameters[allItemFilterParamName] = allItemFilterValue;

      return of({
        resource: simpleSearchConfig.resourceType,
        type: SIMPLE_QUERY,
        params: queryParameters
      });
    }

    if (filterObj) { // In use in quick search when the whole filter object is passed
      const filterItems = this.getFilterItems(filterObj, simpleSearchConfig.simpleSearchWorklistMapping, associationItems);

      if (Array.isArray(filterItems) && filterItems.length > 0) {
        filterItems.forEach(filterItem => {
          if (!isNaN(Date.parse(filterItem.paramValue)) && !Number(filterItem.paramValue)) {
            const datePipe = new DatePipe('en-US');
            filterItem.paramValue = datePipe.transform(filterItem.paramValue, 'yyyy-MM-dd');
          }

          queryParameters[filterItem.paramName] = filterItem.paramValue;
        });
      }

      return of({
        resource: simpleSearchConfig.resourceType,
        type: SIMPLE_QUERY,
        params: queryParameters
      });
    }

    return this.getFilters(simpleSearchConfig.simpleSearchWorklistMapping, associationItems).pipe( // In use when filter panel has data
      take(1),
      switchMap(filterParameters => {
        if (Array.isArray(filterParameters) && filterParameters.length > 0) {
          filterParameters.forEach(filterParameter => queryParameters[filterParameter.paramName] = filterParameter.paramValue);
        }

        return of({
          resource: simpleSearchConfig.resourceType,
          type: SIMPLE_QUERY,
          params: queryParameters
        });
      })
    );
  }

  public search(
    simpleSearchConfig: ISimpleSearchConfig,
    queryOrigin?: string,
    searchText?: string,
    page?: number,
    sortField?: string,
    sortOrder?: number,
    filterObj?: Filter,
    countOnly: boolean = false,
    allItemFilterValue: string = '',
    associationItems: any[] = []
  ) {
    return this.getQuery(
        simpleSearchConfig, queryOrigin, searchText, page, sortField, sortOrder,
        filterObj, countOnly, allItemFilterValue, associationItems
      )
      .pipe(
        take(1),
        switchMap(query => {
          return this.sendRequest(query.resource, query.params);
        })
    );
  }

  public adaptDataToWorklist(data: any) {
    // Mapping data to the worklist columns.
    // For example, in use in case the simple search query result contains a field named 'name' but in the worklist the mapped column field is name_0_text.
    if (!data.data) {
      return;
    }

    if (!data.config || !data.config.simpleSearch || !data.config.simpleSearch.simpleSearchWorklistMapping) {
      console.error('SimpleSearchService->setAssociationData: no simpleSearchWorklistMapping was defined in worklist template');
      return;
    }

    const mapping: Array<ISimpleSearchWorklistMappingItem> = data.config.simpleSearch.simpleSearchWorklistMapping;

    data.data.forEach(dataItem => { // For each item in the result
      mapping.forEach(mapItem => {
        if (!!dataItem[mapItem.nameInSearchResult]) {
          dataItem[mapItem.nameInWorklist] = get(dataItem, mapItem.nameInSearchResult);
        }
      });
    });
  }

  public isSimpleSearch(config: any): boolean {
    return config ? config.queryType && config.queryType.toLowerCase() === SIMPLE_QUERY : false;
  }

  public setAdvancedSearchResult(results, config): any {
    if (results) {
      // Coping searchType to the search result
      results.forEach(item => item.searchType = config.simpleSearchQuery.searchType);
    }

    return results;
  }

  public getFilterParameterNameByNameInWorklist(
    worklistParameterName: string,
    simpleSearchWorklistMapping: ISimpleSearchWorklistMappingItem[]
  ): string {
    // Gets nameInWorklist parameter and return the item name
    let paramName: string = worklistParameterName;

    if (!!paramName && simpleSearchWorklistMapping && simpleSearchWorklistMapping.length > 0) {
      const mappingItem: ISimpleSearchWorklistMappingItem =
        simpleSearchWorklistMapping.find(item => item.nameInWorklist === worklistParameterName);

      if (mappingItem && mappingItem.filterParameterName) {
        paramName = mappingItem.filterParameterName;
      }
    }

    return paramName;
  }

  public requestError(data: any): boolean {
    // Checking if response has error (although Status Code = 200 OK)
    let retVal: boolean = false;

    if (Array.isArray(data) && data.length === 1) {
      const errorMsg: string = get(data, '.issue[0].details', '');

      if (errorMsg) {
        console.error('SimpleSearchService->checkForError: ' + errorMsg);
        retVal = true;
      }
    }

    return retVal;
  }

  private sendRequest(resourceType: string, queryParameters: any) {
    return this._ispmHttpClient.get('entity-tree/' + resourceType, queryParameters);
  }

  private getFilterItems(
    filter: Filter,
    simpleSearchWorklistMapping: ISimpleSearchWorklistMappingItem[],
    associationItems: any[]
  ): ISimpleSearchFilterParameter [] {
    // Parsing filter object. In use when sending a count request for quick filters.
    const filterObjArray: ISimpleSearchFilterParameter [] = [];

    if (!!filter.query) {
      filter.query.split('&').forEach(queryParam => {
        const inx: number = queryParam.indexOf('=');

        if (inx > 0) {
          let paramName: string = queryParam.substring(0, inx);
          const filterItem: ISimpleSearchWorklistMappingItem =
            simpleSearchWorklistMapping.find(item => item.filterComponentName === paramName);

          if (filterItem) {
              paramName = !!filterItem.filterParameterName ? filterItem.filterParameterName : filterItem.filterParameterName;
          } else if (associationItems.length > 0) {
            const associationItem = associationItems.find((item: { name: string}) => item.name === paramName);

            if (!!associationItem) {
              paramName = associationItem.path.replace('{', '').replace('}', ''); // Removing {} which is in use in dynamic search
            }
          }

          if (!!paramName) {
            const paramValue: string = queryParam.substring(inx + 1, queryParam.length);
            filterObjArray.push({ paramName, paramValue: paramValue});
          } else {
            console.error('SimpleSearchService->getFilterItem: missing filter mapping  or associationItem for field: ' + paramName);
          }
        }
      });
    }

    return filterObjArray;
  }

  private getFilters(
    simpleSearchWorklistMapping: ISimpleSearchWorklistMappingItem[],
    associationItems: any[]
  ): Observable<Array<ISimpleSearchFilterParameter>> {
    // Getting filter panel filters from store
    let filterParameters: ISimpleSearchFilterParameter [] = [];

    return this.filterService.getAppliedFilterPropertiesFromStore().pipe(take(1)).map((filters) => {
      if (Array.isArray(filters)) {
        filters.forEach(filter => {
          const filterData: ISimpleSearchFilterParameter[] = this.extractFilterData(filter, simpleSearchWorklistMapping, associationItems);
          filterParameters = filterParameters.concat(filterData);
        });
      }

      return filterParameters;
    });
  }

  private convertDateToUTC(dateStr: string, toDate: boolean): string {
    // User thinks that filter time is his local time, so convert it to UTC, because data is stored in DB in UTC/GMT format
    const convertedDate = toDate ? new Date(dateStr + ' 23:59:59') : new Date(dateStr + ' 00:00:00'); // if toDate = true, adds end day time. If toDate = true (meaning fromDate), adds beginning of the day time.

    return moment(convertedDate).utc().format('YYYY-MM-DDTHH:mm:ss');
  }

  private extractFilterData(
    dynamicFormFilterItem: any,
    simpleSearchWorklistMapping: ISimpleSearchWorklistMappingItem[],
    associationItems: any[]
  ): ISimpleSearchFilterParameter[] {
    // Getting a dynamic form filter element and extracting the filter parameters
    const filterItems: ISimpleSearchFilterParameter[] = [];

    let paramValue: string;
    let fromValueParam: string;
    let toValueParam: string;

    if (dynamicFormFilterItem.type.toLowerCase() === 'datepicker') {
      if (
        dynamicFormFilterItem.selectionMode // Date picker range
        && dynamicFormFilterItem.selectionMode.toLowerCase() === 'range'
        && Array.isArray(dynamicFormFilterItem.selectedValue.value)
        && dynamicFormFilterItem.selectedValue.value.length === 2
      ) {
        fromValueParam =  this.convertDateToUTC(dynamicFormFilterItem.selectedValue.value[0].id, false);
        toValueParam =  this.convertDateToUTC(dynamicFormFilterItem.selectedValue.value[1].id, true);
      } else { // Date picker with a single value
        paramValue = dynamicFormFilterItem.selectedValue.value[0].id;
        const datePipe = new DatePipe('en-US');
        paramValue = datePipe.transform(paramValue, 'yyyy-MM-dd');
      }
    } else {
      paramValue = dynamicFormFilterItem.selectedValue.value.map(item => item.id).join(','); // Support multiple values search
    }

    let mappingItems: ISimpleSearchWorklistMappingItem;

    if (!!simpleSearchWorklistMapping && simpleSearchWorklistMapping.length > 0) {
      mappingItems = simpleSearchWorklistMapping.find(item => item.filterComponentName === dynamicFormFilterItem.name);
    }

    let paramName: string;

    if (mappingItems) {
      paramName = !!mappingItems.filterParameterName ? mappingItems.filterParameterName : mappingItems.nameInSearchResult;
    } else { // If filter item has no special mapping for simple search, use associationItems mapping
      if (associationItems.length > 0) {
        const associationItem = associationItems.find(item => item.tableKey === dynamicFormFilterItem.name ||
          item.name === dynamicFormFilterItem.name ||
          dynamicFormFilterItem.alias?.includes(item.tableKey)
        );

        if (!!associationItem) {
          paramName = associationItem.path.replace('{', '').replace('}', ''); // Removing {} which is in use in dynamic search
        }
      }
    }

    if (!!paramName) {
      if (!!fromValueParam) {
        let paramWithSign: string = paramName + '%3D>';
        filterItems.push({ paramName: paramWithSign, paramValue: fromValueParam});
        paramWithSign = paramName + '%3D<';
        filterItems.push({ paramName: paramWithSign, paramValue: toValueParam});
      } else {
        filterItems.push({ paramName: paramName, paramValue: paramValue});
      }
    } else {
      // tslint:disable-next-line:max-line-length
      console.error('SimpleSearchService->getFilters: missing filter mapping or associationItem for the following field: ' + dynamicFormFilterItem.name);
    }

    return filterItems;
  }
}
